import { Component, inject, OnInit, OnDestroy, HostListener } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule, Router } from '@angular/router';
import { Auth, signOut, user } from '@angular/fire/auth';
import { Firestore, doc, docData } from '@angular/fire/firestore';
import { Subscription, of } from 'rxjs';
import { switchMap } from 'rxjs/operators';

import { UserPortalService } from '../services/user-portal.service';

@Component({
  selector: 'app-home',
  standalone: true,
  imports: [CommonModule, RouterModule],
  templateUrl: './home.component.html',
  styleUrl: './home.component.scss'
})
export class HomeComponent implements OnInit, OnDestroy {
  private auth = inject(Auth);
  private router = inject(Router);
  private firestore = inject(Firestore);
  private portalService = inject(UserPortalService);

  user$ = user(this.auth);
  userProfile: any = null;
  isProfilePopupOpen = false;
  hasRefundRequests = false;
  refundRequestsCount = 0;
  private sub?: Subscription;
  private refundSub?: Subscription;

  ngOnInit() {
    this.sub = this.user$.pipe(
      switchMap(u => {
        if (u) {
          const userDocRef = doc(this.firestore, 'users', u.uid);
          return docData(userDocRef);
        }
        return of(null);
      })
    ).subscribe(profile => {
      this.userProfile = profile;
    });

    this.refundSub = this.user$.pipe(
      switchMap(u => {
        const email = u?.email || localStorage.getItem('userEmail') || '';
        if (email) {
          return this.portalService.getUserRefundRequests(email);
        }
        return of([]);
      })
    ).subscribe(requests => {
      this.refundRequestsCount = requests?.length || 0;
      this.hasRefundRequests = this.refundRequestsCount > 0;
    });
  }

  ngOnDestroy() {
    this.sub?.unsubscribe();
    this.refundSub?.unsubscribe();
  }

  get currentUser() {
    return this.auth.currentUser;
  }

  get userName(): string {
    if (this.userProfile?.displayName) return this.userProfile.displayName;
    if (this.userProfile?.name) return this.userProfile.name;
    if (this.currentUser?.displayName) return this.currentUser.displayName;
    const email = this.userEmail;
    if (email) return email.split('@')[0];
    return 'User';
  }

  get userEmail(): string {
    return this.userProfile?.email || this.currentUser?.email || localStorage.getItem('userEmail') || 'No email provided';
  }

  get userPhotoUrl(): string | null {
    return this.userProfile?.photoURL || this.currentUser?.photoURL || null;
  }

  get userInitials(): string {
    const name = this.userName;
    return name ? name.charAt(0).toUpperCase() : 'U';
  }

  toggleProfilePopup(event?: Event) {
    if (event) {
      event.stopPropagation();
    }
    this.isProfilePopupOpen = !this.isProfilePopupOpen;
  }

  closeProfilePopup() {
    this.isProfilePopupOpen = false;
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: Event) {
    this.isProfilePopupOpen = false;
  }

  get isSessionActive(): boolean {
    return this.router.url.includes('/home/session');
  }

  isMobileMenuOpen = false;

  toggleMobileMenu() {
    this.isMobileMenuOpen = !this.isMobileMenuOpen;
  }

  closeMobileMenu() {
    this.isMobileMenuOpen = false;
  }

  async logout() {
    await signOut(this.auth);
    this.router.navigate(['/login'], { queryParams: { logout: 'true' } });
  }
}

